import { useState, useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { selectData } from 'layouts/onboarding'
import { useSocialSecurityCalculations } from './useSocialSecurityCalculations'
// import { useFegliCalculations } from './useFegliCalculations'
import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
  differenceInDays,
  subMonths,
  parseISO,
} from 'date-fns'
function createData(name, working, retirement) {
  return { name, working, retirement }
}
function findOptimalSickLeave(numberSet, target) {
  return Math.max(...numberSet.filter((num) => num <= target), 0)
}
function getDateDays(date) {
  const newDate = new Date(date)
  return (
    (newDate.getFullYear() * 12 + newDate.getMonth() + 1) * 30 +
    newDate.getDate() +
    1
  )
}
function getFRA(year) {
  if (year < 1938) return 65
  if (year > 1960) return 67

  const table = {
    1938: 65.2,
    1939: 65.3,
    1940: 65.5,
    1941: 65.7,
    1942: 65.8,
    1943: 66.0,
    1944: 66.0,
    1945: 66.0,
    1946: 66.0,
    1947: 66.0,
    1948: 66.0,
    1949: 66.0,
    1950: 66.0,
    1951: 66.0,
    1952: 66.0,
    1953: 66.0,
    1954: 66.0,
    1955: 66.2,
    1956: 66.3,
    1957: 66.5,
    1958: 66.7,
    1959: 66.8,
    1960: 67.0,
  }

  return table[year] || 67
}
function getMinimumRetirementAge(birthYear) {
  if (birthYear < 1948) return { years: 55, months: 0 }
  if (birthYear === 1948) return { years: 55, months: 2 }
  if (birthYear === 1949) return { years: 55, months: 4 }
  if (birthYear === 1950) return { years: 55, months: 6 }
  if (birthYear === 1951) return { years: 55, months: 8 }
  if (birthYear === 1952) return { years: 55, months: 10 }
  if (birthYear >= 1953 && birthYear <= 1964) return { years: 56, months: 0 }
  if (birthYear === 1965) return { years: 56, months: 2 }
  if (birthYear === 1966) return { years: 56, months: 4 }
  if (birthYear === 1967) return { years: 56, months: 6 }
  if (birthYear === 1968) return { years: 56, months: 8 }
  if (birthYear === 1969) return { years: 56, months: 10 }
  return { years: 57, months: 0 }
}
function addRetirementAge(date, { years, months }) {
  let result = addYears(date, years)
  result = addMonths(result, months)
  return result
}
function parseLocalDate(input) {
  if (!input) return null
  if (input instanceof Date) {
    // Already a Date, normalize to local midnight
    return new Date(input.getFullYear(), input.getMonth(), input.getDate())
  }
  if (typeof input === 'string') {
    // Parse "YYYY-MM-DD"
    const [year, month, day] = input.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  throw new Error('parseLocalDate: unsupported input type')
}
function formatLocalDate(date) {
  if (!date) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
export const useDashboardCalculations = (deductions = [], liabilities = []) => {
  const { calculateSSBenefit } = useSocialSecurityCalculations()
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { intakeState } = useContext(IntakeContext)
    // const { basicLife, optionA, optionB, optionC } = useFegliCalculations()
    const clientRetirementAge =
      differenceInMonths(
        new Date(
          intakeState?.dateOfPlannedRetirement == null
            ? new Date()
            : intakeState?.dateOfPlannedRetirement
        ),
        new Date(intakeState?.dateOfBirth)
      ) / 12
    const clientBirthYear = new Date(
      intakeState?.dateOfBirth || new Date()
    ).getFullYear()
    const modeledRetirementAge =
      (new Date(intakeState.dateOfPlannedRetirement) -
        new Date(intakeState.dateOfBirth)) /
      (1000 * 60 * 60 * 24 * 365)

    const dob = new Date(intakeState.dateOfBirth)
    const scd = new Date(intakeState.dateOfHire)
    //Date Values
    const retirementDate =
      intakeState?.dateOfPlannedRetirement == null
        ? new Date()
        : new Date(intakeState.dateOfPlannedRetirement)
    const todaysDate = new Date()
    //Formatted Retirement Date
    const plannedRetirementDate = format(retirementDate, 'MMMM do, yyyy')
    //Days Until Retirement Count - To be used in graphic
    const daysTillRetirement = Math.round(
      (retirementDate.getTime() - todaysDate.getTime()) / (1000 * 60 * 60 * 24)
    )
    const daysSinceRetirement = Math.round(
      (todaysDate.getTime() - retirementDate.getTime()) / (1000 * 60 * 60 * 24)
    )

    const birthYear = new Date(intakeState?.dateOfBirth).getFullYear()
    const minimumRetirementAge =
      birthYear < 1948
        ? 55
        : birthYear === 1948
        ? 55.1667
        : birthYear === 1949
        ? 55.333
        : birthYear === 1950
        ? 55.5
        : birthYear === 1951
        ? 55.667
        : birthYear === 1952
        ? 55.833
        : birthYear >= 1953 && birthYear <= 1964
        ? 56
        : birthYear === 1965
        ? 56.1667
        : birthYear === 1966
        ? 56.333
        : birthYear === 1967
        ? 56.5
        : birthYear === 1968
        ? 56.667
        : birthYear === 1969
        ? 56.833
        : 57
    const minimumRetirementAgeYearsMonths = getMinimumRetirementAge(birthYear)
    const clientFRA = getFRA(birthYear)

    const yearsTillRetirement = (days) => {
      return Math.floor(days / 365)
    }
    const remainingDaysTillRetirement = (days) => {
      return days % 365
    }
    const plannedRetirementMonth = format(retirementDate, 'MMMM')
    const plannedRetirementDay = format(addDays(retirementDate, 1), 'do')
    const plannedRetirementYear = format(retirementDate, 'yyyy')

    //Personal Details
    const personalDetailsName =
      intakeState.firstName + ' ' + intakeState.lastName
    const personalDetailsAgency = intakeState.agency
    const personalDetailsDOB = intakeState.dateOfBirth
    const personalDetailsCYS = 28
    const personalDetailsSCD = intakeState.dateOfHire
    const personalDetailsCurrentAge =
      differenceInMonths(new Date(), new Date(intakeState.dateOfBirth)) / 12
    const personalDetailsPlannedRetirementAge =
      personalDetailsCurrentAge < 63 ? 63 : personalDetailsCurrentAge + 1.5
    const personalDetailsPlannedRetirementDate = format(
      addYears(
        new Date(intakeState.dateOfBirth),
        personalDetailsPlannedRetirementAge
      ),
      'MM/dd/yyyy'
    )
    const estimatedTaxRate = intakeState?.taxRate
      ? intakeState.taxRate / 100
      : 0.2
    // Mandatory Retirement Age
    const mandatoryRetirementAge =
      intakeState?.retirementSystem === 'special_fers_(leo,_atc_etc.)'
        ? 57
        : intakeState?.retirementSystem ===
          'foreign_service_officer_pension_(fsps)'
        ? 65
        : ''

    //Eligibility Scenarios
    const eligibilityScenariosScenario1Age = format(
      addYears(new Date(intakeState.dateOfBirth), 57),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario1YearsofService = format(
      addYears(new Date(intakeState.dateOfHire), 30),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario1Date = format(
      max([
        addYears(new Date(intakeState.dateOfHire), 30),
        addYears(new Date(intakeState.dateOfBirth), 57),
      ]),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario2Age = format(
      addYears(new Date(intakeState.dateOfBirth), 60),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario2YearsofService = format(
      addYears(new Date(intakeState.dateOfHire), 20),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario2Date = format(
      max([
        addYears(new Date(intakeState.dateOfHire), 20),
        addYears(new Date(intakeState.dateOfBirth), 60),
      ]),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario3Age = format(
      addYears(new Date(intakeState.dateOfBirth), 62),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario3YearsofService = format(
      addYears(new Date(intakeState.dateOfHire), 5),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosScenario3Date = format(
      max([
        addYears(new Date(intakeState.dateOfHire), 5),
        addYears(new Date(intakeState.dateOfBirth), 62),
      ]),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosFirstFullyEligibleDate = min([
      max([
        addYears(new Date(intakeState.dateOfHire), 30),
        addRetirementAge(
          new Date(intakeState.dateOfBirth),
          minimumRetirementAgeYearsMonths
        ),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 30),
        addYears(new Date(intakeState.dateOfBirth), 57),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 20),
        addYears(new Date(intakeState.dateOfBirth), 60),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 5),
        addYears(new Date(intakeState.dateOfBirth), 62),
      ]),
    ])
    const eligibilityScenariosFirstFullyEligibleDateSpecialFERS = min([
      max([
        addYears(new Date(intakeState.dateOfHire), 20),
        addYears(new Date(intakeState.dateOfBirth), 50),
      ]),
      addYears(new Date(intakeState.dateOfHire), 25),
    ])
    const eligibilityScenariosFirstFullyEligibleDateFSPS = min([
      max([
        addYears(new Date(intakeState.dateOfHire), 20),
        addYears(new Date(intakeState.dateOfBirth), 50),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 5),
        addYears(new Date(intakeState.dateOfBirth), 62),
      ]),
    ])
    const eligibilityScenariosFirstFullyEligibleDateCSRS = min([
      addYears(new Date(intakeState.dateOfBirth), 55),
    ])
    const eligibilityScenariosFirstFullyEligibleDateCSRSFrozen = min([
      max([
        addYears(new Date(intakeState.dateOfHire), 30),
        addRetirementAge(
          new Date(intakeState.dateOfBirth),
          minimumRetirementAgeYearsMonths
        ),
      ]),
    ])
    // check for all retiement systems with fers being the fallback
    const firstFullyEligibleDate =
      intakeState?.retirementSystem === 'special_fers_(leo,_atc_etc.)'
        ? eligibilityScenariosFirstFullyEligibleDateSpecialFERS
        : intakeState?.retirementSystem ===
          'foreign_service_officer_pension_(fsps)'
        ? eligibilityScenariosFirstFullyEligibleDateFSPS
        : intakeState?.retirementSystem === 'csrs'
        ? eligibilityScenariosFirstFullyEligibleDateCSRS
        : eligibilityScenariosFirstFullyEligibleDate

    const eligibilityScenariosFirstFullyEligibleAge =
      differenceInMonths(
        min([
          max([
            addYears(new Date(intakeState.dateOfHire), 30),
            addYears(new Date(intakeState.dateOfBirth), 57),
          ]),
          max([
            addYears(new Date(intakeState.dateOfHire), 20),
            addYears(new Date(intakeState.dateOfBirth), 60),
          ]),
          max([
            addYears(new Date(intakeState.dateOfHire), 5),
            addYears(new Date(intakeState.dateOfBirth), 62),
          ]),
        ]),
        new Date(intakeState.dateOfBirth)
      ) / 12
    const eligibilityScenariosModeledRetirementDate = format(
      max([
        new Date(),
        min([
          max([
            addYears(new Date(intakeState.dateOfHire), 30),
            addYears(new Date(intakeState.dateOfBirth), 57),
          ]),
          max([
            addYears(new Date(intakeState.dateOfHire), 20),
            addYears(new Date(intakeState.dateOfBirth), 60),
          ]),
          max([
            addYears(new Date(intakeState.dateOfHire), 5),
            addYears(new Date(intakeState.dateOfBirth), 62),
          ]),
        ]),
      ]),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosModeledRetirementAgeSub =
      differenceInMonths(
        max([
          new Date(),
          min([
            max([
              addYears(new Date(intakeState.dateOfHire), 30),
              addYears(new Date(intakeState.dateOfBirth), 57),
            ]),
            max([
              addYears(new Date(intakeState.dateOfHire), 20),
              addYears(new Date(intakeState.dateOfBirth), 60),
            ]),
            max([
              addYears(new Date(intakeState.dateOfHire), 5),
              addYears(new Date(intakeState.dateOfBirth), 62),
            ]),
          ]),
        ]),
        new Date(intakeState.dateOfBirth)
      ) / 12
    const eligibilityScenarioMRA10Date = max([
      addRetirementAge(
        new Date(intakeState.dateOfBirth),
        minimumRetirementAgeYearsMonths
      ),
      addYears(new Date(intakeState.dateOfHire), 10),
    ])
    const eligibilityScenarios62Checker =
      eligibilityScenariosModeledRetirementAgeSub > 60.5 &&
      eligibilityScenariosModeledRetirementAgeSub < 62
        ? 62
        : eligibilityScenariosModeledRetirementAgeSub
    const eligibilityScenarios62CheckerUsed =
      eligibilityScenariosModeledRetirementAgeSub !=
      eligibilityScenarios62Checker
        ? 'YES'
        : 'NO'
    const eligibilityScenariosAdjRetirementDate = format(
      addMonths(
        new Date(intakeState.dateOfBirth),
        eligibilityScenarios62Checker * 12
      ),
      'MM/dd/yyyy'
    )
    const eligibilityScenariosModeledCYSinRetirement =
      differenceInMonths(
        addMonths(
          new Date(intakeState.dateOfBirth),
          eligibilityScenarios62Checker * 12
        ),
        new Date(intakeState.dateOfHire)
      ) / 12

    const eligibilityScenariosActualCYSToday =
      differenceInMonths(new Date(), new Date(intakeState.dateOfHire)) / 12 +
      (intakeState?.militaryActiveDutyPension
        ? 0
        : intakeState?.militaryPaidDeposit
        ? 0
        : differenceInMonths(
            new Date(intakeState?.militaryActiveDutyEndDate || new Date()),
            new Date(intakeState?.militaryActiveDutyStartDate || new Date())
          ) / 12)

    const FERSYearsOfService =
      differenceInMonths(
        new Date(intakeState?.dateOfPlannedRetirement || new Date()),
        new Date(intakeState?.dateOfHire || new Date())
      ) /
        12 +
      (intakeState?.militaryActiveDutyPension
        ? 0
        : intakeState?.militaryPaidDeposit
        ? 0
        : differenceInMonths(
            new Date(intakeState?.militaryActiveDutyEndDate || new Date()),
            new Date(intakeState?.militaryActiveDutyStartDate || new Date())
          ) / 12)

    const militaryTime = intakeState?.militaryPaidDeposit
      ? differenceInMonths(
          new Date(intakeState?.militaryActiveDutyEndDate || new Date()),
          new Date(intakeState?.militaryActiveDutyStartDate || new Date())
        ) / 12
      : 0
    // Include Military Time
    const pureFERSYearsOfService = FERSYearsOfService - militaryTime

    //Pay Stub Details
    const payStubDetailsAnnualSalary = intakeState.baseSalary
    const payStubDetailsHighThree = intakeState.highThree
    const payStubDetailsFICA = intakeState.baseSalary * 0.07
    const payStubDetailsTSPContributionPercentage =
      intakeState.tspContribution + '%'
    const payStubDetailsTSPContributionAmount =
      intakeState.baseSalary * (intakeState.tspContribution / 100)

    const payStubDetailsIncomeTaxes = intakeState.baseSalary * estimatedTaxRate

    const payStubDetailsFEHBCost = intakeState.healthInsurancePremium

    //Math.round(intakeState.baseSalary / 1000) + 2
    const payStubDetailsFEGLIBasicLifeCost =
      (Math.round(intakeState.baseSalary / 1000) + 2) * 0.3467

    const payStubDetailsFEGLIOptionBMultiples = 0
    const payStubDetailsFEGLIOptionBCost =
      dashboardData.fegli[
        Math.round(eligibilityScenariosModeledRetirementAgeSub)
      ][1] *
      Math.round(intakeState.baseSalary / 1000) *
      12 *
      payStubDetailsFEGLIOptionBMultiples
    const payStubDetailsNetTakeHomeAnnual =
      intakeState.baseSalary -
      intakeState.baseSalary * 0.07 -
      intakeState.baseSalary * (intakeState.tspContribution / 100) -
      intakeState.baseSalary * estimatedTaxRate -
      intakeState.healthInsurancePremium -
      payStubDetailsFEGLIBasicLifeCost -
      payStubDetailsFEGLIOptionBCost
    const payStubDetailsNetTakeHomeMonthly =
      payStubDetailsNetTakeHomeAnnual / 12

    const payStubDetailsNetTakeHomePerPayPeriod =
      payStubDetailsNetTakeHomeAnnual / 26

    //Social Security
    const socialSecuritySocialSecurityFullRetirementAge =
      dashboardData.socialsecurityage[format(dob, 'yyyy')]
    const socialSecurityEstimatedGrossMonthlySocialSecurityatAge62Amount =
      intakeState.estimatedSocialSecurity
    const socialSecurityAgeBeginCollectingSocialSecurity = Math.max(
      Math.min(clientRetirementAge, 80),
      62
    )
    const socialSecurityMonthsforR1m1 = Math.max(
      Math.min(
        36,
        (socialSecuritySocialSecurityFullRetirementAge -
          socialSecurityAgeBeginCollectingSocialSecurity) *
          12
      ),
      0
    )
    const socialSecurityMonthsforR2m2 = Math.round(
      Math.max(
        (socialSecuritySocialSecurityFullRetirementAge -
          socialSecurityAgeBeginCollectingSocialSecurity -
          3) *
          12,
        0
      )
    )
    const socialSecurityMonthsforGm3 = Math.max(
      (socialSecurityAgeBeginCollectingSocialSecurity -
        socialSecuritySocialSecurityFullRetirementAge) *
        12,
      0
    )

    const socialSecurityEstimatedSocialSecurityatFRA =
      socialSecurityEstimatedGrossMonthlySocialSecurityatAge62Amount /
      (0.8 -
        (5 / 1200) *
          12 *
          (socialSecuritySocialSecurityFullRetirementAge - 62 - 3))
    const socialSecurityEstimatedSocialSecurityatAge62 =
      intakeState.estimatedSocialSecurity *
      (0.8 -
        (5 / 1200) *
          12 *
          (socialSecuritySocialSecurityFullRetirementAge - 62 - 3))
    const csrsSSadjustment = intakeState?.retirementSystem === 'csrs' ? 480 : 0
    const piaAmmount = intakeState?.estimatedSocialSecurity
    const hasModelInputs =
      intakeState?.socialSecurityModeledAmount != null &&
      intakeState?.socialSecurityModel != null &&
      intakeState?.ageToBeginCollectingSS != null &&
      intakeState?.ageToBeginCollectingSSMonths != null
    const socialSecuritySocialSecurityBenefit =
      intakeState?.alreadyCollectingSocialSecurity
        ? intakeState?.estimatedSocialSecurity
        : hasModelInputs
        ? calculateSSBenefit({
            fraAge: clientFRA || 67,
            baseIncome: intakeState?.socialSecurityModeledAmount || 2400,
            baseType: intakeState?.socialSecurityModel || 'fullRetirementAge',
            claimAgeYears: intakeState?.ageToBeginCollectingSS || 67,
            claimAgeMonths: intakeState?.ageToBeginCollectingSSMonths || 0,
            birthYear: clientBirthYear,
          })
        : piaAmmount *
          (1 -
            (5 / 900) * socialSecurityMonthsforR1m1 -
            (5 / 1200) * socialSecurityMonthsforR2m2 +
            (2 / 300) * socialSecurityMonthsforGm3)
    const socialSecurityBenefitAt62 =
      (intakeState?.estimatedSocialSecurity
        ? intakeState?.estimatedSocialSecurity
        : 2400) *
      (0.8 - (5 / 1200) * 12 * (clientFRA - 62 - 3))
    const socialSecurityEstimatedGrossMonthlySocialSecurityAmount =
      socialSecuritySocialSecurityBenefit
    //At Retirement
    const sickLeaveMonths =
      Math.floor(
        (intakeState?.plannedSickLeave
          ? intakeState?.plannedSickLeave
          : intakeState?.currentSickLeaveBalance || 0) / 174
      ) / 12
    const creditableYearsOfServiceAtRetirement =
      FERSYearsOfService + sickLeaveMonths
    // If person dies while in service sick leave is counted towards pension calc: https://www.opm.gov/faq/retire/Will-I-get-paid-for-unused-sick-leave-in-retirement.ashx
    const creditableYearsOfServiceToday =
      eligibilityScenariosActualCYSToday + sickLeaveMonths
    const monthOfExtraServiceSick =
      ((intakeState?.retirementSystem === 'fers' ? 0.011 : 0.01) / 12 / 12) *
      intakeState?.highThree
    const daysBetweenSCDandRCD =
      getDateDays(
        intakeState?.dateOfPlannedRetirement == null
          ? new Date()
          : intakeState.dateOfPlannedRetirement
      ) - getDateDays(intakeState?.dateOfHire)
    const optimalSickLeave = findOptimalSickLeave(
      selectData.sickLeaveLookup[
        30 - (daysBetweenSCDandRCD % 30 === 0 ? 1 : daysBetweenSCDandRCD % 30)
      ],
      intakeState?.currentSickLeaveBalance
    )

    //FSPS Pension Calc
    const atRetirementGrossPensionFSPS =
      (Math.min(creditableYearsOfServiceAtRetirement * 12, 240) * (0.017 / 12) +
        (creditableYearsOfServiceAtRetirement * 12 - 240) * (0.01 / 12)) *
      intakeState.highThree
    // Special FERS Calc
    // Adjustment for Special FERS
    const atRetirementGrossPensionSpecialFERS =
      (Math.min(creditableYearsOfServiceAtRetirement * 12, 240) * (0.017 / 12) +
        (creditableYearsOfServiceAtRetirement * 12 - 240) * (0.01 / 12)) *
      intakeState.highThree
    // CSRS Pension Calc
    const atRetirementGrossPensionCSRS =
      (Math.min(creditableYearsOfServiceAtRetirement * 12, 60) * (0.015 / 12) +
        (Math.min(creditableYearsOfServiceAtRetirement * 12, 120) === 120
          ? 60
          : creditableYearsOfServiceAtRetirement * 12 - 60) *
          (0.0175 / 12) +
        (Math.min(creditableYearsOfServiceAtRetirement * 12, 503) -
          120 +
          sickLeaveMonths) *
          (0.02 / 12)) *
      intakeState.highThree
    // CSRS Offset Pension Calc
    const atRetirementGrossPensionCSRSOffset =
      atRetirementGrossPensionCSRS - socialSecurityBenefitAt62

    // vera var for both pension and fers supplement
    const VERAparam1 =
      intakeState?.retirementSystem === 'fers' &&
      intakeState?.agency !== 'u.s._postal_service_(usps)' &&
      clientRetirementAge >= 50 &&
      FERSYearsOfService >= 20
    const VERAparam2 =
      intakeState?.retirementSystem === 'fers' &&
      intakeState?.agency !== 'u.s._postal_service_(usps)' &&
      FERSYearsOfService >= 25
    const VERAParam3 =
      intakeState?.retirementSystem === 'foreign_service_officer_pension_(fsps)'
    const atRetirementVERAeligible = VERAparam1 || VERAparam2 || VERAParam3
    const veraRetirementType =
      intakeState?.retirementType === 'vera' ||
      intakeState?.retirementType === 'dsr'
    const veraTypeAndEligible = atRetirementVERAeligible && veraRetirementType

    // zero out pension if you retire before MRA aside from cases like VERA
    const retirementAgeLessThanMRA = clientRetirementAge < minimumRetirementAge
    const retirementAgeLessThanMRAAdjustment = veraTypeAndEligible
      ? 1
      : retirementAgeLessThanMRA
      ? 0
      : 1
    //Regular Pension Calc
    //Swapped out the FERSYearsOfService instead of creditableYearsOfServiceAtRetirement for this calculation
    const atRetirementGrossPensionRegular =
      (clientRetirementAge >= 62 && FERSYearsOfService >= 20 ? 0.011 : 0.01) *
      intakeState.highThree *
      creditableYearsOfServiceAtRetirement *
      retirementAgeLessThanMRAAdjustment
    const marriageTimeWithFormerSpouse =
      intakeState?.familyDivorced && intakeState?.familyDivorcedOwedPension
        ? differenceInMonths(
            new Date(intakeState?.familyDivorcedMarriageEndDate || new Date()),
            new Date(intakeState?.familyDivorcedMarriageStartDate || new Date())
          ) / 12
        : 0
    const formerSpouseOwedPensionPercentage =
      ((marriageTimeWithFormerSpouse / creditableYearsOfServiceAtRetirement) *
        100) /
      2
    // pension reduction amount for MRA10 retirements
    const MRA10ReductionMonths =
      retirementDate < firstFullyEligibleDate &&
      retirementDate >= eligibilityScenarioMRA10Date &&
      !veraRetirementType
        ? differenceInMonths(
            new Date(
              addYears(new Date(intakeState?.dateOfBirth), 62) || new Date()
            ),
            new Date(new Date(retirementDate) || new Date())
          )
        : 0
    // console.log('MRA10ReductionMonths', MRA10ReductionMonths)
    //Gross Pension Calc with MRA adjustments and spouse
    const atRetirementGrossPension =
      (intakeState.retirementSystem === 'foreign_service_officer_pension_(fsps)'
        ? atRetirementGrossPensionFSPS
        : intakeState.retirementSystem === 'special_fers_(leo,_atc_etc.)'
        ? atRetirementGrossPensionSpecialFERS
        : intakeState.retirementSystem === 'csrs'
        ? atRetirementGrossPensionCSRS
        : intakeState.retirementSystem === 'csrs_offset'
        ? atRetirementGrossPensionCSRSOffset
        : atRetirementGrossPensionRegular) *
      (1 - 0.00416667 * MRA10ReductionMonths) *
      (1 - formerSpouseOwedPensionPercentage / 100)

    //FSPS Pension Calc TODAY
    const atRetirementGrossPensionFSPSToday =
      (Math.min(creditableYearsOfServiceToday * 12, 240) * (0.017 / 12) +
        (creditableYearsOfServiceToday * 12 - 240) * (0.01 / 12)) *
      intakeState.highThree
    //Regular Pension Calc TODAY
    const atRetirementGrossPensionRegularToday =
      (personalDetailsCurrentAge >= 62 &&
      eligibilityScenariosActualCYSToday >= 20
        ? 0.011
        : 0.01) *
      intakeState.highThree *
      creditableYearsOfServiceToday
    //FSPS Compare TODAY
    const atRetirementGrossPensionToday =
      intakeState.retirementSystem === 'foreign_service_officer_pension_(fsps)'
        ? atRetirementGrossPensionFSPSToday
        : atRetirementGrossPensionRegularToday

    //Survivor Benefit
    const survivorBenefitCost = intakeState?.survivorBenefitElection
      ? intakeState?.familyMaritalStatus !== 'married'
        ? 0
        : intakeState?.survivorBenefitElection === 'full'
        ? (atRetirementGrossPension * 0.1) / 12
        : intakeState?.survivorBenefitElection === 'partial'
        ? (atRetirementGrossPension * 0.05) / 12
        : 0
      : 0
    const survivorBenefitMonthlyPayout =
      intakeState?.survivorBenefitElection === 'full'
        ? atRetirementGrossPension / 2 / 12
        : intakeState?.survivorBenefitElection === 'partial'
        ? atRetirementGrossPension / 4 / 12
        : 0

    const spouseAge = Math.max(
      20,
      differenceInMonths(
        new Date(),
        intakeState?.familySpouseDateOfBirth
          ? new Date(intakeState.familySpouseDateOfBirth)
          : subMonths(new Date(), 3)
      ) / 12
    )
    const presentValueSurvivorBenefit =
      atRetirementGrossPension /
      (intakeState?.survivorBenefitElection === 'full' ? 2 : 4) /
      dashboardData.fgsafeincomeadvantage[Math.round(Math.max(50, spouseAge))]

    // Present Value of Client Income (Income Coverage)
    const lambdaValue = 0.65
    const adjustedClientIncome =
      payStubDetailsAnnualSalary * lambdaValue * (1 - estimatedTaxRate)
    //lookup uses spouse sex which is not stored - using average
    const presentValueClientIncome =
      adjustedClientIncome *
      dashboardData.pricetables[Math.round(spouseAge)][0].female
    // check to see if their retirement type is vera for DRP people and adjust FERS Supplement accordingly
    const atRetirementFERSSupplement =
      clientRetirementAge < 62 && retirementDate >= firstFullyEligibleDate
        ? (Math.round(pureFERSYearsOfService) / 40) *
          socialSecuritySocialSecurityBenefit
        : 0
    const atRetirementSocialSecurity =
      (atRetirementFERSSupplement === 0 && clientRetirementAge >= 62
        ? socialSecuritySocialSecurityBenefit
        : 0) * 12

    const atRetirementGrossIncome =
      atRetirementGrossPension / 12 +
      (atRetirementVERAeligible && retirementAgeLessThanMRA
        ? 0
        : atRetirementFERSSupplement) +
      atRetirementSocialSecurity / 12
    const atRetirementFEHB =
      ((intakeState.healthInsurancePremium / 80) * 2087) / 12
    const atRetirementBasicLife =
      socialSecurityAgeBeginCollectingSocialSecurity >= 65
        ? 0
        : payStubDetailsFEGLIBasicLifeCost
    const atRetirementIncomeTaxes =
      (atRetirementGrossPension * estimatedTaxRate) / 12
    const atRetirementSSTaxes =
      (atRetirementSocialSecurity * estimatedTaxRate * 0.85) / 12
    const atRetirementNetTakeHomeAnnual =
      atRetirementGrossIncome -
      atRetirementFEHB -
      atRetirementBasicLife -
      atRetirementIncomeTaxes
    const atRetirementNetTakeHomeMonthly =
      atRetirementGrossIncome -
      atRetirementFEHB -
      atRetirementBasicLife -
      atRetirementIncomeTaxes -
      atRetirementSSTaxes

    //TSP Balance
    const tspBalanceTraditionalTSPBalance = intakeState.tspBalance

    //Incomes Comparison
    const incomesComparisonNetTakeHomeWhileWorkingAnnual =
      payStubDetailsNetTakeHomeAnnual
    const incomesComparisonInRetirementAnnual = atRetirementNetTakeHomeAnnual
    const incomesComparisonAnnualDifference =
      incomesComparisonNetTakeHomeWhileWorkingAnnual -
      incomesComparisonInRetirementAnnual
    const incomesComparisonNetTakeHomeWhileWorkingMonthly =
      payStubDetailsNetTakeHomeMonthly
    const incomesComparisonInRetirementMonthly = atRetirementNetTakeHomeMonthly
    const incomesComparisonMonthlyDIfference =
      incomesComparisonNetTakeHomeWhileWorkingMonthly -
      incomesComparisonInRetirementMonthly
    const incomesComparisonEffectiveHourlyWage =
      incomesComparisonMonthlyDIfference / 174

    const incomePriceEligibility62 =
      dashboardData.pricetables[Math.round(eligibilityScenarios62Checker)]

    // should there be a default index here?
    const getIncomePriceAtIndex = (index) => incomePriceEligibility62?.[index]
    //Balance Analysis
    const traditionaltspbalance = intakeState.tspBalance
    const incomeprice0cola =
      (getIncomePriceAtIndex(0).male + getIncomePriceAtIndex(0).female) / 2
    const incomeprice3cola =
      (getIncomePriceAtIndex(3).male + getIncomePriceAtIndex(3).female) / 2
    const fgsafeincomeadvantage35increasing =
      1 /
      dashboardData.fgsafeincomeadvantage[
        Math.round(eligibilityScenarios62Checker)
      ]
    const traditionalfundsneededtobridgegap0cola =
      (incomesComparisonAnnualDifference < 0
        ? 0
        : incomeprice0cola * incomesComparisonAnnualDifference) /
      (1 - estimatedTaxRate)
    const traditionalfundsneededtobridgegap3cola =
      (incomesComparisonAnnualDifference < 0
        ? 0
        : incomeprice3cola * incomesComparisonAnnualDifference) /
      (1 - estimatedTaxRate)
    const traditionalfundsneededtobridgegapfgsafeincomeadvantage35increasing =
      (incomesComparisonAnnualDifference < 0
        ? 0
        : fgsafeincomeadvantage35increasing *
          incomesComparisonAnnualDifference) /
      (1 - estimatedTaxRate)
    const oftspfundsrequired0cola =
      traditionalfundsneededtobridgegap0cola / intakeState.tspBalance
    const oftspfundsrequired3cola =
      traditionalfundsneededtobridgegap3cola / intakeState.tspBalance
    const offundsneededforfg =
      traditionalfundsneededtobridgegapfgsafeincomeadvantage35increasing /
      intakeState.tspBalance

    //Net Income Today Calc
    const netIncomeTodayCalculated =
      ((intakeState.netTakeHome / 80) * 2087) / 12
    //Net Incomt in Retirement Calc
    const netIncomeInRetirementCalculated = atRetirementNetTakeHomeMonthly
    //Simple Gap
    const gapCalculated =
      netIncomeTodayCalculated - netIncomeInRetirementCalculated

    const readinessScore =
      intakeState.tspBalance /
      (((gapCalculated * 12) / (1 - estimatedTaxRate)) *
        ((dashboardData.pricetables[
          Math.round(socialSecurityAgeBeginCollectingSocialSecurity)
        ][3].male +
          dashboardData.pricetables[
            Math.round(socialSecurityAgeBeginCollectingSocialSecurity)
          ][3].female) /
          2))

    const readinessScoreAdjusted =
      readinessScore >= 1 || readinessScore < 0 ? 0.98 : readinessScore

    const govSSTaxAmount = 160200
    const ssTaxPercent = 0.062
    const medicareTaxPercent = 0.0145
    // Create Pension Lookup Table to define
    const pensionAdjuster = new Date(intakeState?.dateOfHire)
    const pensionContribution =
      pensionAdjuster < new Date('2013-1-1')
        ? 0.008
        : pensionAdjuster < new Date('2014-1-1')
        ? 0.031
        : 0.044
    const ssTaxBaseSalaryAmount = Math.min(
      intakeState.baseSalary * ssTaxPercent,
      govSSTaxAmount * ssTaxPercent
    )
    const medicareTaxBaseSalaryAmount =
      intakeState.baseSalary * medicareTaxPercent
    const pensionContributionAmount =
      intakeState.baseSalary * pensionContribution
    const tspContributionAmount =
      intakeState.baseSalary * (intakeState.tspContribution / 100)
    const estimatedSocialSecurityBenefit = socialSecuritySocialSecurityBenefit

    //normalized gross values
    const normalizedGrossWhileWorking =
      (intakeState.baseSalary -
        ssTaxBaseSalaryAmount -
        medicareTaxBaseSalaryAmount -
        pensionContributionAmount -
        tspContributionAmount) /
      12

    const normalizedGrossInRetirement =
      atRetirementGrossIncome - survivorBenefitCost

    //estimated taxes
    const estimatedTaxesWhileWorking =
      normalizedGrossWhileWorking * estimatedTaxRate

    const fersSupplementorSocialSecurity =
      atRetirementVERAeligible && retirementAgeLessThanMRA
        ? 0
        : atRetirementFERSSupplement === 0
        ? estimatedSocialSecurityBenefit
        : atRetirementFERSSupplement
    const fersSupplementorSocialSecurityTaxRate =
      atRetirementFERSSupplement === 0
        ? estimatedTaxRate * 0.85
        : estimatedTaxRate

    const estimatedTaxesInRetirement =
      (atRetirementGrossPension / 12 - survivorBenefitCost) * estimatedTaxRate +
      fersSupplementorSocialSecurity * fersSupplementorSocialSecurityTaxRate

    //deductions
    const healthInsuranceCost = intakeState?.healthInsurancePremium
      ? ((intakeState.healthInsurancePremium / 80) * 2087) / 12
      : 0
    //FEGLI Calcs
    // const basicLife = intakeState?.fegli
    //   ? intakeState?.agency === 'u.s._postal_service_(usps)'
    //     ? 0
    //     : 0.3467 * (Math.ceil(intakeState?.baseSalary / 1000) + 2)
    //   : 0
    // const optionA = intakeState?.fegliOptionA
    //   ? selectData.fegli[Math.floor(personalDetailsCurrentAge)].optionA
    //   : 0
    // const optionBCoverage =
    //   intakeState?.fegliOptionBMultiples *
    //   (Math.ceil(intakeState?.baseSalary / 1000) * 1000)
    // const optionB = intakeState?.fegliOptionB
    //   ? selectData.fegli[Math.floor(personalDetailsCurrentAge)].optionB *
    //     (optionBCoverage / 1000)
    //   : 0
    // const optionC = intakeState?.fegliOptionC
    //   ? selectData.fegli[Math.floor(personalDetailsCurrentAge)].optionC *
    //     intakeState.fegliOptionCMultiples
    //   : 0
    // const basicLifeCost = basicLife?.premium
    // const basicLifeRetirementCost = basicLife?.retirementPremium
    // const optionACost = optionA?.premium
    // const optionARetirementCost = optionA?.retirementPremium
    // const optionBCost = optionB?.premium
    // const optionBRetirementCost = optionB?.retirementPremium
    // const optionCCost = optionC?.premium
    // const optionCRetirementCost = optionC?.retirementPremium

    const { deductionTypes } = selectData
    const deductionSubTypeLabelMap = deductionTypes.reduce((summ, type) => {
      summ[type.value] = type.label

      return summ
    }, {})

    const otherDeductions = [
      ...deductions
        .filter(
          (deduction) =>
            deduction.subType !== 'alotchecking' &&
            deduction.subType !== 'fsa' &&
            deduction.subType !== 'hsa'
        )
        .map(({ amount, subType, keepInRetirement }) => {
          const retirementAmount = keepInRetirement ? amount : 0
          return createData(
            deductionSubTypeLabelMap[subType],
            ((amount / 80) * 2087) / 12,
            ((retirementAmount / 80) * 2087) / 12
          )
        }),
    ]
    const healthInsuranceRow = otherDeductions.push(
      createData('Health Insurance', healthInsuranceCost, healthInsuranceCost)
    )
    // const basicLifeRow =
    //   basicLife &&
    //   otherDeductions.push(
    //     createData('Basic Life', basicLifeCost, basicLifeRetirementCost)
    //   )
    // const optionARow =
    //   optionA &&
    //   otherDeductions.push(
    //     createData('Option A', optionACost, optionARetirementCost)
    //   )
    // const optionBRow =
    //   optionB &&
    //   otherDeductions.push(
    //     createData('Option B', optionBCost, optionBRetirementCost)
    //   )
    // const optionCRow =
    //   optionC &&
    //   otherDeductions.push(
    //     createData('Option C', optionCCost, optionCRetirementCost)
    //   )
    const estimatedTaxesRow = otherDeductions.push(
      createData(
        'Estimated Taxes',
        estimatedTaxesWhileWorking,
        estimatedTaxesInRetirement
      )
    )

    const otherDeductionTotals = otherDeductions.reduce(
      (summ, deduction) => {
        summ.working += +deduction.working
        summ.retirement += +deduction.retirement
        return summ
      },
      { working: 0, retirement: 0 }
    )

    const cashAddbackDeductions = [
      ...deductions
        .filter(
          (deduction) =>
            deduction.subType === 'alotchecking' ||
            deduction.subType === 'fsa' ||
            deduction.subType === 'hsa'
        )
        .map(({ amount, subType, keepInRetirement }) => {
          const retirementAmount = keepInRetirement ? amount : 0
          return createData(
            deductionSubTypeLabelMap[subType],
            ((amount / 80) * 2087) / 12,
            ((retirementAmount / 80) * 2087) / 12
          )
        }),
    ]

    const cashaddbackDeductionTotals = cashAddbackDeductions.reduce(
      (summ, deduction) => {
        summ.working += +deduction.working
        summ.retirement += +deduction.retirement
        return summ
      },
      { working: 0, retirement: 0 }
    )

    const adjustedNetPayWorking =
      normalizedGrossWhileWorking - otherDeductionTotals.working
    const adjustedNetPayRetirement =
      normalizedGrossInRetirement - otherDeductionTotals.retirement
    const workingNetTakeHome = intakeState?.netTakeHome
      ? ((intakeState?.netTakeHome / 80) * 2087) / 12
      : 0
    const adjustedWorkingNetTakeHome =
      workingNetTakeHome +
      cashaddbackDeductionTotals.working +
      (intakeState?.alreadyCollectingSocialSecurity
        ? intakeState?.estimatedSocialSecurity
        : 0)
    // swap out adjustedNetPayWorking for workingNetTakeHome
    const normalizedGrossGap =
      adjustedWorkingNetTakeHome - adjustedNetPayRetirement

    return {
      netIncomeTodayCalculated,
      netIncomeInRetirementCalculated,
      gapCalculated,
      retirementDate,
      daysTillRetirement,
      daysSinceRetirement,
      yearsTillRetirement,
      remainingDaysTillRetirement,
      plannedRetirementMonth,
      plannedRetirementDay,
      plannedRetirementYear,
      personalDetailsCurrentAge,
      personalDetailsPlannedRetirementAge,
      eligibilityScenariosModeledRetirementAgeSub,
      eligibilityScenarios62Checker,
      payStubDetailsFEGLIOptionBMultiples,
      eligibilityScenariosFirstFullyEligibleDate,
      eligibilityScenariosFirstFullyEligibleDateSpecialFERS,
      eligibilityScenariosFirstFullyEligibleDateFSPS,
      eligibilityScenarioMRA10Date,
      firstFullyEligibleDate,
      atRetirementGrossIncome,
      eligibilityScenariosModeledCYSinRetirement,
      socialSecurityEstimatedSocialSecurityatFRA,
      socialSecurityEstimatedSocialSecurityatAge62,
      socialSecuritySocialSecurityBenefit,
      FERSYearsOfService,
      atRetirementBasicLife,
      atRetirementIncomeTaxes,
      atRetirementGrossPension,
      atRetirementFERSSupplement,
      atRetirementSocialSecurity,
      atRetirementSSTaxes,
      atRetirementFEHB,
      readinessScoreAdjusted,
      creditableYearsOfServiceAtRetirement,
      creditableYearsOfServiceToday,
      normalizedGrossWhileWorking,
      normalizedGrossInRetirement,
      normalizedGrossGap,
      adjustedWorkingNetTakeHome,
      adjustedNetPayRetirement,
      atRetirementGrossPensionToday,
      presentValueSurvivorBenefit,
      presentValueClientIncome,
      survivorBenefitCost,
      survivorBenefitMonthlyPayout,
      atRetirementVERAeligible,
      veraTypeAndEligible,
      retirementAgeLessThanMRA,
      minimumRetirementAge,
      monthOfExtraServiceSick,
      optimalSickLeave,
      formerSpouseOwedPensionPercentage,
      clientRetirementAge,
    }
  } catch (e) {
    console.log(e)
    return {}
  }
}
